import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, User, LogOut, Home, Info, Phone, Globe, Moon, Sun, ChevronRight, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isDarkMode, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const langMenuRef = useRef(null);
  
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'te', name: 'Telugu' },
    { code: 'hi', name: 'Hindi' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
  ];
  
  const { isAuthenticated, isAdmin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setIsLangMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
  };

  const navItems = [
    { name: t('header.home'), path: '/', icon: Home },
    { name: t('header.about'), path: '/about', icon: Info },
    { name: t('header.contact'), path: '/contact', icon: Phone },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top Banner */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-primary-600 to-brand-gold text-white text-center py-1.5 px-4 shadow-md">
        <p className="text-xs sm:text-sm font-bold uppercase tracking-widest flex items-center justify-center gap-2">
          🎉 FREE Registration for Tutors and Parents
          <Link to="/register" className="underline hover:text-primary-100 transition-colors ml-1 hidden sm:inline">Register Now</Link>
        </p>
      </div>

      <header className="fixed top-0 inset-x-0 z-50 flex justify-center mt-10 px-4 transition-all duration-300">
        <nav className={`w-full max-w-6xl rounded-3xl transition-all duration-300 border ${
          isScrolled 
            ? 'bg-white/90 dark:bg-brand-navy/90 backdrop-blur-xl shadow-2xl border-white/20 dark:border-white/10 py-3 px-6' 
            : 'bg-white/70 dark:bg-brand-navy/70 backdrop-blur-md shadow-lg border-white/20 dark:border-white/10 py-4 px-6'
        }`}>
          <div className="flex items-center justify-between relative">
            
            {/* Left: Logo */}
            <Link to="/" className="flex items-center space-x-3 z-50 group">
              <div className="bg-primary-100 p-2 rounded-2xl group-hover:scale-105 transition-transform">
                <img src="/images/logo.jpg" alt="Quadra Tech Logo" className="h-8 w-8 object-contain rounded-xl" />
              </div>
              <div className="hidden sm:flex flex-col">
                <span className="font-extrabold text-lg text-slate-900 dark:text-white leading-none tracking-tight">Quadra Tech</span>
                <span className="text-[10px] text-primary-600 dark:text-primary-400 font-bold uppercase tracking-widest mt-1">Tuitions</span>
              </div>
            </Link>

            {/* Center: Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8 absolute left-1/2 transform -translate-x-1/2">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`text-sm font-semibold transition-colors flex items-center gap-1.5 hover:text-primary-600 dark:hover:text-primary-400 ${
                    isActive(item.path) ? 'text-primary-600 dark:text-primary-400' : 'text-slate-600 dark:text-slate-300'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center space-x-2 z-50">
              {/* Theme Toggle (Desktop) */}
              <button onClick={toggleTheme} className="hidden sm:flex p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 transition-colors">
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {/* Language (Desktop) */}
              <div className="relative hidden sm:block" ref={langMenuRef}>
                <button 
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                  className="flex items-center gap-1.5 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 transition-colors text-sm font-semibold uppercase"
                >
                  <Globe className="w-4 h-4" />
                  <span>{i18n.language?.split('-')[0] || 'en'}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>

                {/* Language Dropdown */}
                <AnimatePresence>
                  {isLangMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-40 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 overflow-hidden py-2"
                    >
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            i18n.changeLanguage(lang.code);
                            setIsLangMenuOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm font-semibold transition-colors hover:bg-slate-50 dark:hover:bg-slate-700 ${
                            (i18n.language || 'en').split('-')[0] === lang.code 
                              ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20' 
                              : 'text-slate-700 dark:text-slate-300'
                          }`}
                        >
                          {lang.name}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* CTA Button / Auth */}
              <div className="hidden md:block border-l border-slate-200 dark:border-slate-700 pl-4 ml-1">
                {isAuthenticated ? (
                  <div className="flex items-center space-x-2">
                    <Link to={isAdmin ? "/admin" : "/dashboard"} className="p-2 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 transition-colors">
                      <User className="w-5 h-5" />
                    </Link>
                    <button onClick={handleLogout} className="p-2 rounded-full bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 transition-colors">
                      <LogOut className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <Link to="/register" className="flex items-center gap-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-5 py-2.5 rounded-full text-sm font-bold hover:scale-105 transition-transform shadow-lg">
                    Join Now <ChevronRight className="w-4 h-4" />
                  </Link>
                )}
              </div>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white"
              >
                {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Dropdown */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                className="md:hidden absolute top-[calc(100%+1rem)] left-0 w-full bg-white dark:bg-brand-navy rounded-3xl shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden"
              >
                <div className="p-4 space-y-2">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center gap-3 p-3 rounded-2xl font-semibold transition-colors ${
                          isActive(item.path) ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        {item.name}
                      </Link>
                    );
                  })}
                  
                  <div className="my-4 h-px bg-slate-100 dark:bg-slate-800" />

                  <div className="flex items-center justify-between p-3">
                    <span className="font-semibold text-slate-700 dark:text-slate-300">Theme</span>
                    <button onClick={toggleTheme} className="p-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                      {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                    </button>
                  </div>

                  {/* Language Selector (Mobile) */}
                  <div className="p-3">
                    <span className="font-semibold text-slate-700 dark:text-slate-300 mb-2 block">Language</span>
                    <div className="flex flex-wrap gap-2">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            i18n.changeLanguage(lang.code);
                            setIsMenuOpen(false);
                          }}
                          className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ${
                            (i18n.language || 'en').split('-')[0] === lang.code
                              ? 'bg-primary-600 text-white'
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                          }`}
                        >
                          {lang.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {isAuthenticated ? (
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      <Link to={isAdmin ? "/admin" : "/dashboard"} onClick={() => setIsMenuOpen(false)} className="flex justify-center items-center gap-2 bg-slate-100 dark:bg-slate-800 p-3 rounded-2xl font-semibold text-slate-700 dark:text-slate-300">
                        <User className="w-4 h-4" /> {isAdmin ? "Admin Panel" : "Dashboard"}
                      </Link>
                      <button onClick={handleLogout} className="flex justify-center items-center gap-2 bg-red-50 dark:bg-red-500/10 p-3 rounded-2xl font-semibold text-red-600 dark:text-red-400">
                        <LogOut className="w-4 h-4" /> Logout
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      <Link to="/login" onClick={() => setIsMenuOpen(false)} className="text-center p-3 rounded-2xl font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                        Login
                      </Link>
                      <Link to="/register" onClick={() => setIsMenuOpen(false)} className="text-center p-3 rounded-2xl font-semibold bg-primary-600 text-white">
                        Register
                      </Link>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      </header>
    </>
  );
};

export default Header; 

